
import { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { FacebookPixel } from "@/lib/facebookPixel";

type AuthContextType = {
  user: User | null;
  session: Session | null;
};

const AuthContext = createContext<AuthContextType>({ user: null, session: null });

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("AuthProvider: Initial session check");
    
    // First set up the auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("AuthProvider: Auth state changed", { 
        event: _event,
        hasSession: !!session,
        userId: session?.user?.id
      });
      
      setSession(session);
      setUser(session?.user ?? null);
      
      // Track Facebook Pixel events for authentication
      if (_event === 'SIGNED_IN' && session?.user) {
        // Track sign up/login - we'll track both as CompleteRegistration
        FacebookPixel.trackSignUp();
      }
      
      // If this is a SIGNED_OUT event, ensure we clear any cached data
      if (_event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
      }
    });

    // Then check for an existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("AuthProvider: Session retrieved", { 
        hasSession: !!session,
        userId: session?.user?.id 
      });
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    console.log("AuthProvider: Still loading");
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  console.log("AuthProvider: Rendering with auth context", {
    hasUser: !!user,
    userId: user?.id
  });

  return (
    <AuthContext.Provider value={{ user, session }}>
      {children}
    </AuthContext.Provider>
  );
};
